import React from 'react';
import SubPage from '../components/site/SubPage';
import { BarChart, IC3_CATEGORICAL, TrendChart } from '../components/site/Charts';
import {
  CenteredStatTile,
  ClosingBrand,
  PageSection,
  PageTitleBox,
  SectionHeading,
  SourceLine,
  StatTile,
} from '../components/site/SubPageParts';

const IMG = '/images/site/happens-to-other-people';

// Pew Research findings, laid out as the mockup's 3 x 2 plate grid.
const PEW_STATS = [
  { value: '73%', background: '#A6A6A6', body: 'experienced some kind of online scam or attack' },
  { value: '1 in 5', background: '#EF6458', body: 'report losing money to an online scam or attack.' },
  {
    value: '3 in 10',
    background: '#5F5F5F',
    body: 'who’ve lost money say it had a significant impact on their finances',
  },
  { value: '79%', background: '#E7A985', body: 'describe online scammers as a major problem.' },
  {
    value: '74%',
    background: '#EEBEA5',
    body: 'who’ve lost money to an online scam never contacted law enforcement',
  },
  {
    value: '8 out 10',
    background: '#C8665E',
    body: 'older adults are likely to fall victim to online scams',
  },
];

const IC3_60_PLUS = [
  { background: '#A6A6A6', lines: ['201,266', 'Complaints UP', '37% FROM 2024'] },
  { background: '#7F7F7F', lines: ['$7.748 Billion in Losses UP', '59% FROM 2024'] },
  { background: '#595959', lines: ['$38,500', 'Average Loss'] },
  { background: '#000000', lines: ['12,444 Complainants Lost', '> $100K'] },
];

/*
 * Figures below are transcribed from the 2025 IC3 Annual Report (FBI),
 * https://www.ic3.gov/AnnualReport/Reports/2025_IC3Report.pdf
 *
 * Complaints by Year (p.4) labels only these six years and carries no y-axis,
 * so the other nineteen are not recoverable from the chart.
 */
const COMPLAINTS_BY_YEAR = [
  { label: '2001', value: 49711, display: '49,711' },
  { label: '2005', value: 231493, display: '231,493' },
  { label: '2010', value: 303809, display: '303,809' },
  { label: '2015', value: 288012, display: '288,012' },
  { label: '2020', value: 791790, display: '791,790' },
  { label: '2025', value: 1008597, display: '1,008,597' },
];

const LOSSES_BY_YEAR = [
  { label: '2001', value: 0.0178, display: '$17.8M' },
  { label: '2005', value: 0.183, display: '$183M' },
  { label: '2010', value: 1.1, display: '$1.1B' },
  { label: '2015', value: 1.0, display: '$1.0B' },
  { label: '2020', value: 4.2, display: '$4.2B' },
  { label: '2021', value: 6.9, display: '$6.9B' },
  { label: '2022', value: 10.3, display: '$10.3B' },
  { label: '2023', value: 12.5, display: '$12.5B' },
  { label: '2024', value: 16.6, display: '$16.6B' },
  { label: '2025', value: 20.877, display: '$20.877B' },
];

const AGE_RANGES = [
  { label: 'No Age', value: 16.4, display: '16.4%' },
  { label: '60+', value: 24.2, display: '24.2%' },
  { label: '50 - 59', value: 13.1, display: '13.1%' },
  { label: '40 - 49', value: 14.5, display: '14.5%' },
  { label: '30 - 39', value: 14.3, display: '14.3%' },
  { label: '20 - 29', value: 13.2, display: '13.2%' },
  { label: '20/Under', value: 4.3, display: '4.3%' },
];

// BEC was dropped per the 8-22 review, so this is no longer a top-five list.
const TOP_BY_LOSS = [
  { label: 'Investment', value: 8648617756, display: '$8,648,617,756' },
  { label: 'Tech Support', value: 2134675818, display: '$2,134,675,818' },
  { label: 'Confidence/Romance', value: 929287469, display: '$929,287,469' },
  { label: 'Government Impersonation', value: 797943193, display: '$797,943,193' },
];

const AGE_ROWS = [
  { group: '30 – 39', complaints: '153,293', losses: '$1.74 Billion' },
  { group: '40 – 49', complaints: '167,066', losses: '$2.95 Billion' },
  { group: '50 – 59', complaints: '124,820', losses: '$3.67 Billion' },
  { group: '60+', complaints: '201,266', losses: '$7.74 Billion' },
];

const HappensToOtherPeople = () => (
  <SubPage title="Happens to other people">
    <PageTitleBox>
      Scams happen to other people.
    </PageTitleBox>

    <PageSection className="pt-4">
      <img
        src={`${IMG}/hero.jpeg`}
        alt="A smiling older woman in a white shirt"
        className="h-[220px] w-full object-cover object-right small:aspect-[16/5] small:h-auto"
      />
    </PageSection>

    {/* ---------------- Pew findings ---------------- */}
    <PageSection className="pt-8">
      <SectionHeading>You sure about that?</SectionHeading>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 lg:gap-4">
        {PEW_STATS.map((stat) => (
          <StatTile key={stat.value} value={stat.value} background={stat.background}>
            {stat.body}
          </StatTile>
        ))}
      </div>
      <div className="mt-4">
        <SourceLine>
          Source &gt; Pew Research &gt; Online Scams Attacks in America Today &gt; 07 / 31 / 2025.
        </SourceLine>
      </div>
    </PageSection>

    {/* ---------------- 60+ trendline ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>60+ Trendline.</SectionHeading>
      {/* items-stretch, and the image sized off the row rather than a fixed
          height, so the photo and the stat column start and finish together. */}
      <div className="mt-5 grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2 lg:gap-10">
        <img
          src={`${IMG}/trendline-60plus.jpeg`}
          alt="An older couple at a laptop with a hooded figure standing behind them"
          className="h-[240px] w-full object-cover small:aspect-[652/365] small:h-auto lg:aspect-auto lg:h-full"
        />
        <div className="flex flex-col">
          <h3 className="bg-black px-4 py-4 text-center text-[22px] font-bold text-white md:px-5 md:py-5 md:text-[30px]">
            2025 IC3 60+ complaints.
          </h3>
          <div className="mt-5 grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4">
            {IC3_60_PLUS.map((tile) => (
              <CenteredStatTile
                key={tile.lines.join(' ')}
                lines={tile.lines}
                background={tile.background}
              />
            ))}
          </div>
        </div>
      </div>
    </PageSection>

    {/* ---------------- Demographics ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>IC3 2025 Abuse Demographics.</SectionHeading>
      <img
        src={`${IMG}/demographics-row.jpeg`}
        alt="A row of eleven people of varying ages and backgrounds"
        className="mt-5 w-full object-cover"
      />
    </PageSection>

    {/* ---------------- IC3 report charts ---------------- */}
    <PageSection className="pt-12">
      <div className="bg-[#F2F2F2] px-4 py-8 md:px-10 md:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <TrendChart
            title="Complaints by Year"
            points={COMPLAINTS_BY_YEAR}
            note="Complaints filed with IC3 — labelled years only"
            axisFormat={(v) => (v === 0 ? '0' : `${Math.round(v / 1000)}k`)}
          />
          <TrendChart
            title="Losses by Year"
            points={LOSSES_BY_YEAR}
            note="Reported losses, USD billions"
            axisFormat={(v) => (v === 0 ? '$0' : `$${v}B`)}
          />
          <BarChart
            title="Age Ranges of Cyber-Enabled Fraud Reporting"
            bars={AGE_RANGES}
            valueHeading="Share of reports"
            textured
          />
          <BarChart
            title="Top Cyber-Enabled crime by losses."
            bars={TOP_BY_LOSS}
            colors={IC3_CATEGORICAL}
            labelPlacement="inline"
            valueHeading="Losses"
          />
        </div>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-center">
            <thead>
              <tr className="bg-[#2E5C8A] text-white">
                <th className="px-4 py-3 text-[16px] font-normal md:text-[20px]">Age Groups</th>
                <th className="px-4 py-3 text-[16px] font-normal md:text-[20px]">Complaint</th>
                <th className="px-4 py-3 text-[16px] font-normal md:text-[20px]">$$$ Losses</th>
              </tr>
            </thead>
            <tbody>
              {AGE_ROWS.map((row, index) => (
                <tr key={row.group} className={index % 2 === 0 ? 'bg-[#D3D8DE]' : 'bg-[#E8ECEF]'}>
                  <td className="px-4 py-2 text-[15px] text-black md:text-[19px]">{row.group}</td>
                  <td className="px-4 py-2 text-[15px] text-black md:text-[19px]">
                    {row.complaints}
                  </td>
                  <td className="px-4 py-2 text-[15px] text-black md:text-[19px]">{row.losses}</td>
                </tr>
              ))}
              <tr className="bg-[#D3D8DE]">
                <td className="px-4 py-2 text-[15px] text-black md:text-[19px]">Total &gt;&gt;</td>
                <td className="px-4 py-2 text-[15px] text-black md:text-[19px]">646,445</td>
                <td className="px-4 py-2 text-[15px] text-black md:text-[19px]">$16.1 Billion</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <SourceLine>
            Source: FBI Internet Crime Report | Age Group 0 &ndash; 29 are excluded.
          </SourceLine>
        </div>
      </div>
    </PageSection>

    {/* ---------------- Closing ---------------- */}
    {/* 8-27: headline centred above the photo, the photo whole at its own
        3:1 aspect on every device, and the brand block (wordmark, rule,
        tagline) beneath — nothing is laid over the image at any width. */}
    <PageSection className="pt-12">
      {/* The break is set by hand below md: left to the measure the line
          wrapped after "Numbers" and orphaned "don't lie" on its own row. */}
      <p className="text-center text-[24px] font-bold leading-tight text-black md:text-[42px]">
        You sure about that?{' '}
        <br className="md:hidden" />
        The numbers don&rsquo;t lie.
      </p>
      <img
        src={`${IMG}/numbers-dont-lie.jpeg`}
        alt="A woman with red glasses looking sceptically at the viewer"
        className="mt-4 h-auto w-full md:mt-6"
      />
    </PageSection>

    <ClosingBrand />
  </SubPage>
);

export default HappensToOtherPeople;
